import styles from "./HoverWord.module.scss";
import { BIG_H, useWordBox, type OverlayProps } from "./shared";

/* Timings travel as custom properties, not inline animation-delay, so the
   scss can run several delayed animations per element (and the exit rules
   can override them) without fighting inline style. */
const v = (vars: Record<string, string | number>) =>
  vars as React.CSSProperties;

type IconKey = "webhook" | "agent" | "notion" | "slack";

/* n8n's own node icons (packages/nodes-base), recoloured through theme
   vars where they'd vanish on the dark canvas; the agent is n8n's robot
   glyph, redrawn. Each is drawn in its source viewBox. */
const ICONS: Record<IconKey, [number, React.ReactNode]> = {
  webhook: [
    48,
    <>
      <path
        fill="var(--n8-ink)"
        d="M35 37c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4"
      />
      <path
        fill="var(--n8-ink)"
        d="M35 43c-3 0-5.9-1.4-7.8-3.7l3.1-2.5c1.1 1.4 2.9 2.3 4.7 2.3 3.3 0 6-2.7 6-6s-2.7-6-6-6c-1 0-2 .3-2.9.7l-1.7 1L23.3 16l3.5-1.9 5.3 9.4c1-.3 2-.5 3-.5 5.5 0 10 4.5 10 10S40.5 43 35 43"
      />
      <path
        fill="var(--n8-ink)"
        d="M14 43C8.5 43 4 38.5 4 33c0-4.6 3.1-8.5 7.5-9.7l1 3.9C9.9 27.9 8 30.3 8 33c0 3.3 2.7 6 6 6s6-2.7 6-6v-2h15v4H23.8c-.9 4.6-5 8-9.8 8"
      />
      <path
        fill="var(--n8-hook)"
        d="M14 37c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4"
      />
      <path
        fill="var(--n8-ink)"
        d="M25 19c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4"
      />
      <path
        fill="var(--n8-hook)"
        d="m15.7 34-3.4-2 5.9-9.7c-2-1.9-3.2-4.5-3.2-7.3 0-5.5 4.5-10 10-10s10 4.5 10 10c0 .9-.1 1.7-.3 2.5l-3.9-1c.1-.5.2-1 .2-1.5 0-3.3-2.7-6-6-6s-6 2.7-6 6c0 2.1 1.1 4 2.9 5.1l1.7 1z"
      />
    </>,
  ],
  agent: [
    24,
    <g stroke="var(--n8-ink)" strokeWidth="1.8" strokeLinecap="round">
      <rect x="4.5" y="8" width="15" height="11" rx="3" fill="none" />
      <path d="M12 8V5M2.5 12v3M21.5 12v3" />
      <circle cx="12" cy="4" r="1.5" fill="var(--n8-ink)" stroke="none" />
      <circle cx="9.3" cy="13.2" r="1.5" fill="var(--n8-ink)" stroke="none" />
      <circle cx="14.7" cy="13.2" r="1.5" fill="var(--n8-ink)" stroke="none" />
    </g>,
  ],
  notion: [
    40,
    <path
      fill="var(--n8-ink)"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.58276 6.97679C8.82047 7.98238 9.28479 7.90566 11.6091 7.75057L33.5206 6.43488C33.9853 6.43488 33.5989 5.97127 33.4439 5.89423L29.8049 3.26348C29.1076 2.72213 28.1786 2.10217 26.3982 2.25726L5.18115 3.80476C4.40736 3.88148 4.25282 4.26837 4.56096 4.57847L7.58276 6.97679ZM8.89829 12.0833V35.1381C8.89829 36.3771 9.51746 36.8407 10.911 36.764L34.9919 35.3706C36.3862 35.2939 36.5415 34.4417 36.5415 33.4352V10.5351C36.5415 9.53019 36.1549 8.98829 35.3014 9.06564L10.1367 10.5351C9.20799 10.6131 8.89821 11.0777 8.89821 12.0833H8.89829ZM32.6708 13.32C32.8252 14.017 32.6708 14.7133 31.9725 14.7917L30.8123 15.0229V32.0434C29.8049 32.5848 28.8759 32.8944 28.1018 32.8944C26.8625 32.8944 26.5521 32.5072 25.6237 31.3474L18.0343 19.4329V30.9605L20.4359 31.5024C20.4359 31.5024 20.4359 32.8944 18.4983 32.8944L13.1568 33.2042C13.0016 32.8944 13.1568 32.1214 13.6986 31.9665L15.0925 31.5802V16.3385L13.1572 16.1834C13.0019 15.4864 13.3885 14.4814 14.4733 14.4035L20.2035 14.0172L28.1018 26.0868V15.4097L26.0881 15.1786C25.9335 14.3265 26.5521 13.7078 27.3265 13.6311L32.6708 13.32ZM3.39973 1.71598L25.4688 0.0907457C28.179 -0.141688 28.8763 0.0140245 30.5796 1.25135L37.6243 6.20276C38.7867 7.05421 39.1742 7.28602 39.1742 8.21419V35.3706C39.1742 37.0726 38.5542 38.0791 36.3865 38.2331L10.7577 39.7807C9.13049 39.8583 8.35607 39.6264 7.50392 38.5426L2.31608 31.8117C1.38658 30.5726 1 29.6457 1 28.5613V4.42283C1 3.03105 1.62019 1.87005 3.39973 1.71598V1.71598Z"
    />,
  ],
  slack: [
    150.852,
    <g transform="translate(.926 .926)" strokeWidth="1.852">
      <path
        fill="#e01e5a"
        stroke="#e01e5a"
        d="M40.741 93.55c0-8.735 6.607-15.772 14.815-15.772s14.815 7.037 14.815 15.772v38.824c0 8.737-6.607 15.774-14.815 15.774s-14.815-7.037-14.815-15.772z"
      />
      <path
        fill="#ecb22d"
        stroke="#ecb22d"
        d="M93.55 107.408c-8.735 0-15.772-6.607-15.772-14.815s7.037-14.815 15.772-14.815h38.826c8.735 0 15.772 6.607 15.772 14.815s-7.037 14.815-15.772 14.815z"
      />
      <path
        fill="#2fb67c"
        stroke="#2fb67c"
        d="M77.778 15.772C77.778 7.037 84.385 0 92.593 0s14.815 7.037 14.815 15.772v38.826c0 8.735-6.607 15.772-14.815 15.772s-14.815-7.037-14.815-15.772z"
      />
      <path
        fill="#36c5f1"
        stroke="#36c5f1"
        d="M15.772 70.371C7.037 70.371 0 63.763 0 55.556s7.037-14.815 15.772-14.815h38.826c8.735 0 15.772 6.607 15.772 14.815s-7.037 14.815-15.772 14.815z"
      />
      <path
        fill="#ecb22d"
        stroke="#ecb22d"
        d="M77.778 133.333c0 8.208 6.607 14.815 14.815 14.815s14.815-6.607 14.815-14.815-6.607-14.815-14.815-14.815H77.778z"
      />
      <path
        fill="#2fb67c"
        stroke="#2fb67c"
        d="M133.334 70.371h-14.815V55.556c0-8.207 6.607-14.815 14.815-14.815s14.815 6.607 14.815 14.815-6.607 14.815-14.815 14.815z"
      />
      <path
        fill="#e01e5a"
        stroke="#e01e5a"
        d="M14.815 77.778H29.63v14.815c0 8.207-6.607 14.815-14.815 14.815S0 100.8 0 92.593s6.607-14.815 14.815-14.815z"
      />
      <path
        fill="#36c5f1"
        stroke="#36c5f1"
        d="M70.371 14.815V29.63H55.556c-8.207 0-14.815-6.607-14.815-14.815S47.348 0 55.556 0s14.815 6.607 14.815 14.815z"
      />
    </g>,
  ],
};

/* n8n's trigger marker, the lightning bolt beside a trigger node */
const BOLT = "M13 1.5 3.5 13.5h7l-1 9 10-12.5h-7z";

interface Node {
  key: "t" | "a" | "n" | "s";
  icon: IconKey;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

const f = (n: number) => n.toFixed(2);

/* n8n quotation: an automation, not a prompt chain. A webhook fires, the
   AI Agent qualifies the lead, and the run fans out to Notion (logged)
   and Slack (team pinged). The phrase steps back while the canvas builds
   in n8n's own grammar — D-shaped trigger with its bolt, the wide agent
   node, bar inputs and dot outputs, arrowed wires over the dot grid —
   then executes the way n8n shows a run: each node spins, turns green
   with a check, and its outgoing wire goes green under a "1 item" label.
   The end state stays, as n8n leaves it. Everything centres on the
   line's cap axis. Small instances run a linear trigger → agent → Slack
   row without labels or grid; on exit the canvas un-builds in reverse
   (the scss drives that from --i / --n). */
export function FlowOverlay({ hostRef, hot }: OverlayProps) {
  const box = useWordBox(hostRef);
  if (!box) return null;
  const { w, h, axis: mid } = box;
  const big = h >= BIG_H;
  /* Node names need width as well as height: a 360px phone's headline
     keeps the fork but drops the names, so the stack stays clear of the
     line below. */
  const named = big && w >= 340;

  /* n8n proportions scaled down (100px node : 2px border : 8px radius). */
  const s = big ? Math.max(20, Math.min(34, h * 0.36)) : Math.round(h * 0.5);
  const bw = big ? 1.4 : 1.1;
  const rad = Math.max(2.5, s * 0.1);
  const ico = s * (big ? 0.46 : 0.5);
  const lab = Math.max(8.5, Math.min(10, s * 0.32));
  /* The agent sizes to its own name (Inter 500 "AI Agent" ≈ 4.3em) with
     real padding either side, so a phone's smaller node never jams the
     label into the output port. */
  const agentW = big
    ? Math.max(s * 2.9, s * 0.22 + ico * 1.24 + 4 + lab * 4.3 + s * 0.45)
    : s * 1.7;
  const boltW = big ? s * 0.34 : 0;

  const tx = boltW + 2;
  const rx = w - s - 1;
  const ax = (tx + s + rx) / 2 - agentW / 2;
  const row = mid - s / 2;
  const nodes: Node[] = [
    { key: "t", icon: "webhook", label: "Webhook", x: tx, y: row, w: s, h: s },
    { key: "a", icon: "agent", label: "AI Agent", x: ax, y: row, w: agentW, h: s },
  ];
  if (big) {
    /* a full label of air between the pair, so the fork never crowds */
    const sep = s / 2 + (named ? lab / 2 + 10 : 6);
    nodes.push(
      { key: "n", icon: "notion", label: "Notion", x: rx, y: mid - sep - s / 2, w: s, h: s },
      { key: "s", icon: "slack", label: "Slack", x: rx, y: mid + sep - s / 2, w: s, h: s },
    );
  } else {
    nodes.push({ key: "s", icon: "slack", label: "Slack", x: rx, y: row, w: s, h: s });
  }
  const at = Object.fromEntries(nodes.map((n) => [n.key, n])) as Record<
    Node["key"],
    Node
  >;

  /* Timeline (ms): the canvas builds, then executes — the trigger fires,
     the agent thinks longest, the fan-out runs in parallel. */
  const E = big ? 640 : 520;
  const T = {
    t: { d: 0, run: E, ok: E + 240 },
    a: { d: 110, run: E + 300, ok: E + 900 },
    n: { d: 220, run: E + 960, ok: E + 1240 },
    s: { d: big ? 270 : 220, run: E + 960, ok: big ? E + 1300 : E + 1240 },
  };

  const edges: Array<[Node["key"], Node["key"]]> = big
    ? [
        ["t", "a"],
        ["a", "n"],
        ["a", "s"],
      ]
    : [
        ["t", "a"],
        ["a", "s"],
      ];
  const n = nodes.length;
  const gridId = `n8grid-${Math.round(w)}-${Math.round(h)}`;

  return (
    <span className={styles.overlay} data-out={hot ? "0" : "1"} aria-hidden>
      <svg
        className={`${styles.svg} ${styles.n8}`}
        viewBox={`0 0 ${w} ${h}`}
        style={v({ "--n": n })}
      >
        {big && (
          <>
            <defs>
              <pattern
                id={`${gridId}p`}
                width="11"
                height="11"
                patternUnits="userSpaceOnUse"
                y={mid % 11}
              >
                <circle className={styles.n8Dot} cx="5.5" cy="5.5" r="0.75" />
              </pattern>
              <radialGradient id={`${gridId}g`}>
                <stop offset=".55" stopColor="#fff" />
                <stop offset="1" stopColor="#fff" stopOpacity="0" />
              </radialGradient>
              <mask id={`${gridId}m`}>
                <rect
                  x={-24}
                  y={-18}
                  width={w + 48}
                  height={h + 36}
                  fill={`url(#${gridId}g)`}
                />
              </mask>
            </defs>
            <rect
              className={styles.n8Grid}
              x={-24}
              y={-18}
              width={w + 48}
              height={h + 36}
              fill={`url(#${gridId}p)`}
              mask={`url(#${gridId}m)`}
            />
          </>
        )}

        {edges.map(([a, b], i) => {
          const A = at[a];
          const B = at[b];
          const x0 = A.x + A.w + 3;
          const y0 = A.y + A.h / 2;
          const x1 = B.x - 4;
          const y1 = B.y + B.h / 2;
          const dx = Math.max(10, (x1 - x0) * 0.5);
          const d = `M${f(x0)} ${f(y0)} C${f(x0 + dx)} ${f(y0)} ${f(x1 - dx)} ${f(y1)} ${f(x1)} ${f(y1)}`;
          const draw = a === "t" ? 150 : 300;
          const ok = T[a].ok + 40;
          const ah = big ? 3.4 : 2.4;
          /* A straight wire carries its count at the midpoint. On the fork
             the midpoint is the steepest part of the S, so the label rides
             the flat run into the target instead, ending a clear gap
             before the arrowhead. */
          const fork = Math.abs(y1 - y0) >= 1;
          const below = y1 > y0 + 1;
          const lx = fork ? x1 - ah * 1.3 - 9 : (x0 + x1) / 2;
          const ly = fork ? y1 : y0;
          return (
            <g key={`e${i}`} className={styles.n8Edge} style={v({ "--i": i })}>
              <path
                className={styles.n8Wire}
                d={d}
                pathLength={1}
                strokeWidth={bw * 0.9}
                style={v({ "--d": `${draw}ms` })}
              />
              <path
                className={styles.n8WireOk}
                d={d}
                pathLength={1}
                strokeWidth={bw * 0.9}
                style={v({ "--ok": `${ok}ms` })}
              />
              <path
                className={styles.n8Head}
                d={`M${f(x1 + 1)} ${f(y1)} l${f(-ah * 1.3)} ${f(-ah)} v${f(ah * 2)}z`}
                style={v({ "--d": `${draw + 200}ms`, "--ok": `${ok + 160}ms` })}
              />
              {big && (
                <text
                  className={styles.n8Item}
                  x={f(lx)}
                  y={f(below ? ly + lab + 1 : ly - 4)}
                  textAnchor={fork ? "end" : "middle"}
                  fontSize={lab - 1}
                  style={v({ "--ok": `${ok + 120}ms` })}
                >
                  1 item
                </text>
              )}
            </g>
          );
        })}

        {nodes.map((node, i) => {
          const t = T[node.key];
          const vars = v({
            "--d": `${t.d}ms`,
            "--run": `${t.run}ms`,
            "--ok": `${t.ok}ms`,
            "--i": i,
          });
          const wide = node.key === "a";
          const trig = node.key === "t";
          const cx = wide ? node.x + ico * 0.62 + s * 0.22 : node.x + node.w / 2;
          const cy = node.y + node.h / 2;
          const [vb, glyph] = ICONS[node.icon];
          /* the trigger is D-shaped: n8n rounds a trigger's left side */
          const R = node.h / 2;
          const right = node.x + node.w;
          const bottom = node.y + node.h;
          const dBody = `M${f(node.x + R)} ${f(node.y)} H${f(right - rad)} Q${f(right)} ${f(node.y)} ${f(right)} ${f(node.y + rad)} V${f(bottom - rad)} Q${f(right)} ${f(bottom)} ${f(right - rad)} ${f(bottom)} H${f(node.x + R)} A${f(R)} ${f(R)} 0 0 1 ${f(node.x + R)} ${f(node.y)}Z`;
          const check = big || node.key === "s";
          const k = big ? s * 0.17 : s * 0.24;
          const kx = right - k - (big ? 2.6 : 1.8);
          const ky = bottom - k - (big ? 2.2 : 1.6);
          return (
            <g key={node.key} className={styles.n8Node} style={vars}>
              {trig ? (
                <path className={styles.n8Body} d={dBody} strokeWidth={bw} />
              ) : (
                <rect
                  className={styles.n8Body}
                  x={node.x}
                  y={node.y}
                  width={node.w}
                  height={node.h}
                  rx={rad}
                  strokeWidth={bw}
                />
              )}
              {/* ports: input bar on the left, output dot on the right */}
              {!trig && (
                <rect
                  className={styles.n8Port}
                  x={node.x - 2.4}
                  y={cy - s * 0.13}
                  width={2}
                  height={s * 0.26}
                  rx={0.6}
                />
              )}
              {(trig || wide) && (
                <circle
                  className={styles.n8Port}
                  cx={right + 0.2}
                  cy={cy}
                  r={big ? 2.3 : 1.7}
                />
              )}
              <g className={styles.n8Icon}>
                <svg
                  x={cx - ico / 2}
                  y={cy - ico / 2}
                  width={ico}
                  height={ico}
                  viewBox={`0 0 ${vb} ${vb}`}
                  overflow="visible"
                >
                  {glyph}
                </svg>
              </g>
              <circle
                className={styles.n8Spin}
                cx={cx}
                cy={cy}
                r={ico * 0.34}
                strokeWidth={big ? 1.6 : 1.2}
                strokeDasharray={`${f(ico * 1.4)} 99`}
              />
              {wide && big && (
                <text
                  className={styles.n8Label}
                  x={cx + ico * 0.62 + 4}
                  y={cy + lab * 0.36}
                  fontSize={lab}
                >
                  {node.label}
                </text>
              )}
              {named && !wide && (
                <text
                  className={styles.n8Label}
                  x={node.x + node.w / 2}
                  y={bottom + lab + 2.5}
                  textAnchor="middle"
                  fontSize={lab}
                >
                  {node.label}
                </text>
              )}
              {trig && big && (
                <path
                  className={styles.n8Bolt}
                  d={BOLT}
                  transform={`translate(${f(node.x - boltW - 1.5)} ${f(cy - boltW / 2)}) scale(${(boltW / 24).toFixed(4)})`}
                />
              )}
              {/* success check, bottom-right inside the node, as n8n has it */}
              {check && (
                <path
                  className={styles.n8Check}
                  d={`M${f(kx)} ${f(ky + k * 0.55)} l${f(k * 0.35)} ${f(k * 0.35)} l${f(k * 0.65)} ${f(-k * 0.75)}`}
                  strokeWidth={big ? 1.6 : 1.2}
                />
              )}
            </g>
          );
        })}
      </svg>
    </span>
  );
}
